import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Star, MessageSquare, X } from "lucide-react";
import {
  adminListTestimonials,
  adminCreateTestimonial,
  adminUpdateTestimonial,
  adminDeleteTestimonial,
} from "../../lib/api";
import { toast } from "sonner";

const EMPTY = {
  quote: "",
  author: "",
  role: "",
  company: "",
  avatar: "",
  logo: "",
  rating: 5,
  is_active: true,
};

const TestimonialsAdminPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminListTestimonials();
      setItems(d.items || []);
    } catch {
      toast.error("Konnte Testimonials nicht laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing("new");
    setForm(EMPTY);
  };
  const startEdit = (t) => {
    setEditing(t.id);
    setForm({ ...EMPTY, ...t });
  };
  const cancel = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!form.quote?.trim() || !form.author?.trim()) {
      toast.error("Zitat und Autor sind Pflicht");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        quote: form.quote,
        author: form.author,
        role: form.role || "",
        company: form.company || "",
        avatar: form.avatar || "",
        logo: form.logo || "",
        rating: Number(form.rating) || 5,
        is_active: !!form.is_active,
      };
      if (editing === "new") {
        await adminCreateTestimonial(payload);
        toast.success("Testimonial erstellt");
      } else {
        await adminUpdateTestimonial(editing, payload);
        toast.success("Testimonial aktualisiert");
      }
      await load();
      cancel();
    } catch {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Testimonial wirklich löschen?")) return;
    try {
      await adminDeleteTestimonial(id);
      toast.success("Gelöscht");
      await load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="text-white">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-pink-400">
            <MessageSquare size={12} /> Backoffice
          </div>
          <h1 className="mt-2 font-display font-extrabold text-3xl md:text-4xl tracking-tight">
            Testimonials
          </h1>
          <p className="mt-2 text-sm text-neutral-400">
            Kundenstimmen verwalten – erscheinen auf jeder Public-Seite.
          </p>
        </div>
        <button
          onClick={startNew}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-neutral-950 text-xs font-bold uppercase tracking-[0.12em] hover:bg-gradient-to-r hover:from-pink-500 hover:via-orange-500 hover:to-purple-500 hover:text-white transition-all"
          data-testid="testimonials-new-button"
        >
          <Plus size={14} /> Neu
        </button>
      </div>

      {editing && (
        <div className="mb-8 p-6 rounded-2xl bg-white/[0.04] border border-white/10 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">
              {editing === "new" ? "Neues Testimonial" : "Testimonial bearbeiten"}
            </h2>
            <button onClick={cancel} className="text-neutral-400 hover:text-white">
              <X size={18} />
            </button>
          </div>

          <textarea
            placeholder="Zitat (was der Kunde sagt)"
            value={form.quote}
            onChange={(e) => setForm({ ...form, quote: e.target.value })}
            rows={4}
            className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 outline-none focus:border-pink-400/60 text-sm"
            data-testid="testimonial-quote-input"
          />

          <div className="grid md:grid-cols-2 gap-4">
            <input
              placeholder="Autor / Name"
              value={form.author}
              onChange={(e) => setForm({ ...form, author: e.target.value })}
              className="px-4 py-3 rounded-xl bg-black/40 border border-white/10 outline-none focus:border-pink-400/60 text-sm"
              data-testid="testimonial-author-input"
            />
            <input
              placeholder="Rolle (z.B. Inhaber)"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="px-4 py-3 rounded-xl bg-black/40 border border-white/10 outline-none focus:border-pink-400/60 text-sm"
            />
            <input
              placeholder="Firma"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="px-4 py-3 rounded-xl bg-black/40 border border-white/10 outline-none focus:border-pink-400/60 text-sm"
            />
            <input
              placeholder="Avatar URL (optional)"
              value={form.avatar}
              onChange={(e) => setForm({ ...form, avatar: e.target.value })}
              className="px-4 py-3 rounded-xl bg-black/40 border border-white/10 outline-none focus:border-pink-400/60 text-sm"
            />
            <input
              placeholder="Firmenlogo URL (optional)"
              value={form.logo}
              onChange={(e) => setForm({ ...form, logo: e.target.value })}
              className="px-4 py-3 rounded-xl bg-black/40 border border-white/10 outline-none focus:border-pink-400/60 text-sm"
            />
            <div className="flex items-center gap-4">
              <label className="text-xs uppercase tracking-widest text-neutral-400">Rating</label>
              <select
                value={form.rating}
                onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}
                className="px-4 py-3 rounded-xl bg-black/40 border border-white/10 outline-none focus:border-pink-400/60 text-sm"
              >
                {[5, 4, 3, 2, 1].map((r) => (
                  <option key={r} value={r}>
                    {r} Sterne
                  </option>
                ))}
              </select>
              <label className="ml-auto inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                Aktiv
              </label>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white text-xs font-bold uppercase tracking-[0.12em] disabled:opacity-60"
              data-testid="testimonial-save-button"
            >
              <Save size={14} /> {saving ? "Speichert…" : "Speichern"}
            </button>
            <button
              onClick={cancel}
              className="px-6 py-3 rounded-full border border-white/10 text-xs uppercase tracking-[0.12em] font-bold text-neutral-300 hover:bg-white/5"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-neutral-400 text-sm">Laden…</div>
      ) : items.length === 0 ? (
        <div className="p-10 rounded-2xl border border-dashed border-white/10 text-center text-neutral-400 text-sm">
          Noch keine Testimonials.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((t) => (
            <div
              key={t.id}
              className={`p-5 rounded-2xl border transition-colors ${
                t.is_active
                  ? "bg-white/[0.04] border-white/10"
                  : "bg-white/[0.02] border-white/5 opacity-60"
              }`}
              data-testid={`testimonial-item-${t.id}`}
            >
              <div className="flex items-center gap-1 mb-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    size={12}
                    className={
                      i < (t.rating || 0)
                        ? "fill-amber-400 text-amber-400"
                        : "fill-neutral-700 text-neutral-700"
                    }
                  />
                ))}
                {!t.is_active && (
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-neutral-500">
                    inaktiv
                  </span>
                )}
              </div>
              <p className="text-sm text-neutral-200 leading-relaxed">
                „{t.quote}&rdquo;
              </p>
              <div className="mt-4 text-xs text-neutral-400">
                <b className="text-white">{t.author}</b>
                {t.role ? ` · ${t.role}` : ""}
                {t.company ? ` · ${t.company}` : ""}
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => startEdit(t)}
                  className="px-4 py-2 rounded-full border border-white/10 text-[11px] uppercase tracking-widest font-bold hover:bg-white/5"
                >
                  Bearbeiten
                </button>
                <button
                  onClick={() => remove(t.id)}
                  className="p-2 rounded-full border border-white/10 text-red-400 hover:bg-red-500/10"
                  title="Löschen"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TestimonialsAdminPage;
